import { useNavigate, useParams } from "@solidjs/router";
import {
  createEffect,
  createResource,
  createSignal,
  type JSX,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import type { PaymentMethodView, PlaceLabel, Position, TripHistoryItem } from "../../shared/api";
import { api } from "../api";
import { AppBar } from "../components/AppBar";
import { ComingSoonToast } from "../components/ComingSoon";
import { ReportChoiceSheet } from "../components/ReportSheets";
import { reportHref } from "../report";
import { OperatedBy } from "../components/OperatedBy";
import { ScreenLoader } from "../components/Spinner";
import { directions } from "../directions";
import { km, minutes } from "../format";
import { clockLabel, paidWithLabel, priceLines, receiptDate, seenRides } from "../history";
import { createMapView, type MapView } from "../map/view";
import { session } from "../state/session";

/** Address of a point, or undefined (no address, quota, offline): the time stands alone then. */
const reverse = (p: Position | undefined) =>
  p
    ? api
        .get<PlaceLabel>(`/places/reverse?at=${p.lng},${p.lat}`)
        .then((r) => r.name)
        .catch(() => undefined)
    : Promise.resolve(undefined);

/**
 * Figma H3 "Détail de l'achat": static map of the ride, date / bike / departure / arrival /
 * duration · distance, price breakdown, payment line. The route is Pony's direction API between
 * the start and the end (an approximation until Pony sends the real track); addresses come from
 * reverse geocoding. The PDF download (H3b) is out of scope.
 */
export function Receipt() {
  const navigate = useNavigate();
  const [reportOpen, setReportOpen] = createSignal(false);
  const params = useParams<{ id: string }>();
  onMount(async () => {
    if (!(await session.known()))
      navigate(`/signin?next=${encodeURIComponent(`/historique/${params.id}`)}`, {
        replace: true,
      });
  });
  const [fresh] = createResource(
    () => params.id,
    (id) => api.get<TripHistoryItem>(`/trips/${encodeURIComponent(id)}/receipt`),
  );
  /** The list's copy shows at once; the BFF's replaces it when it lands. */
  const ride = () => (fresh.state === "ready" ? fresh() : undefined) ?? seenRides.get(params.id);
  const [method] = createResource(() =>
    api.get<PaymentMethodView | null>("/payments/method").catch(() => null),
  );
  const [fromName] = createResource(() => ride()?.from, reverse);
  const [toName] = createResource(() => ride()?.to, reverse);

  return (
    <main class="screen bg-surface">
      <AppBar title="Détail de l'achat" back="/historique" />
      <Show
        when={ride()}
        fallback={
          <Show when={fresh.error} fallback={<ScreenLoader label="Chargement du reçu…" />}>
            <div class="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
              <p class="text-[15px] leading-[1.25] text-strong">Reçu introuvable.</p>
              <button
                type="button"
                class="h-11 text-sm text-link underline"
                onClick={() => navigate("/historique")}
              >
                Retour à l'historique
              </button>
            </div>
          </Show>
        }
      >
        {(r) => {
          const lines = () => priceLines(r());
          const paid = () => paidLine(r(), method());
          return (
            <div class="screen-body pb-[max(24px,env(safe-area-inset-bottom))]">
              <RideMap from={r().from} to={r().to} path={r().path} />
              <dl class="mt-[18px] flex flex-col gap-[12px] px-5">
                <Row label="Date" value={receiptDate(r().startedAt)} />
                <Row label="Vélo" value={r().vehicleId} />
                <Row label="Départ" value={withPlace(clockLabel(r().startedAt), fromName())} />
                <Row label="Arrivée" value={withPlace(clockLabel(r().endedAt), toName())} />
                <Row
                  label="Durée · distance"
                  value={withPlace(
                    minutes(r().durationSec),
                    r().distanceMeters !== undefined ? km(r().distanceMeters ?? 0) : undefined,
                  )}
                />
              </dl>
              <div class="mx-5 mt-[18px] border-t border-border pt-[13px]">
                <Show when={lines().unlock}>
                  {(u) => <PriceRow label="Déverrouillage" value={u()} />}
                </Show>
                <Show when={lines().billed}>
                  {(b) => (
                    <PriceRow class="mt-[10px]" label={b()} value={lines().billedAmount ?? ""} />
                  )}
                </Show>
                <div
                  class={`${lines().unlock ? "mt-[14px]" : ""} flex justify-between text-base font-semibold leading-[1.25] tracking-[0.16px] text-strong`}
                >
                  <span>Total</span>
                  <span>{lines().total}</span>
                </div>
                <Show when={paid()}>
                  {(p) => (
                    <p class="mt-[6px] text-xs leading-[1.25] tracking-[0.12px] text-text">{p()}</p>
                  )}
                </Show>
              </div>
              <OperatedBy class="mt-[40px]" />
              {/* "Télécharger le reçu (PDF)" (H3b) is out of scope: hidden. */}
              <div class="mt-auto flex justify-center pt-8">
                <button
                  type="button"
                  class="h-[34px] text-sm leading-[1.25] tracking-[0.14px] text-link underline"
                  onClick={() => setReportOpen(true)}
                >
                  Signaler un problème
                </button>
                <ReportChoiceSheet
                  open={reportOpen()}
                  onClose={() => setReportOpen(false)}
                  href={reportHref({
                    vehicle: r().vehicleId,
                    trip: r().id,
                    back: `/historique/${r().id}`,
                  })}
                />
              </div>
            </div>
          );
        }}
      </Show>
      <ComingSoonToast />
    </main>
  );
}

const withPlace = (a: string, b: string | undefined) => (b ? `${a} · ${b}` : a);

/** "Payé avec Apple Pay" (Figma), the card, or the Pony app for rides made there. */
function paidLine(r: TripHistoryItem, m: PaymentMethodView | null | undefined) {
  if (!r.priceCents) return undefined;
  const how = paidWithLabel(r, m);
  if (!how) return undefined;
  if (!r.inApp) return "Payé dans l'app Pony";
  return how === "Apple Pay" ? "Payé avec Apple Pay" : `Payé par ${how.toLowerCase()}`;
}

/** Figma H3 detail row: Regular 14 #666665 label, Medium 14 #49454f value right-aligned (w 200). */
function Row(props: { label: string; value: string }) {
  return (
    <div class="flex items-baseline justify-between gap-4 text-sm leading-[1.25] tracking-[0.14px]">
      <dt class="shrink-0 text-text">{props.label}</dt>
      <dd class="max-w-[220px] truncate text-right font-medium text-strong">{props.value}</dd>
    </div>
  );
}

function PriceRow(props: { label: string; value: string; class?: string }) {
  return (
    <div
      class={`flex justify-between gap-3 text-sm leading-[1.25] tracking-[0.14px] text-text ${props.class ?? ""}`}
    >
      <span>{props.label}</span>
      <span class="shrink-0">{props.value}</span>
    </div>
  );
}

/**
 * Figma H3 map thumbnail, 190 px, static: bike pin at the start, parking pin at the end, violet
 * ride line: the real track (Pony leg progressGeometry), else the direction API's route, else a
 * straight line.
 */
function RideMap(props: {
  from?: Position | undefined;
  to?: Position | undefined;
  path?: Position[] | undefined;
}): JSX.Element {
  let container!: HTMLDivElement;
  const [view, setView] = createSignal<MapView>();
  onMount(async () => {
    const center = props.from ?? props.to;
    if (!center) return;
    const v = await createMapView({ container, center, onSelect: () => undefined });
    setView(v);
  });
  onCleanup(() => view()?.destroy());
  createEffect(() => {
    const v = view();
    const a = props.from;
    const b = props.to;
    if (!v) return;
    if (a)
      v.setVehicles([
        { id: "ride-start", type: "ebike", lat: a.lat, lng: a.lng, battery: 1, rangeMeters: 0 },
      ]);
    if (b)
      v.setParking({
        parking: [{ id: "ride-end", name: "Arrivée", lat: b.lat, lng: b.lng }],
        forbidden: [],
      });
    if (!a || !b) {
      if (a ?? b) v.flyTo((a ?? b) as Position, 15);
      return;
    }
    let live = true;
    onCleanup(() => {
      live = false;
    });
    const draw = (path: Position[]) => {
      if (!live) return;
      v.setItinerary({ ride: path });
      const lngs = path.map((p) => p.lng);
      const lats = path.map((p) => p.lat);
      v.fitBounds(
        [
          [Math.min(...lngs), Math.min(...lats)],
          [Math.max(...lngs), Math.max(...lats)],
        ],
        { top: 36, right: 36, bottom: 24, left: 36 },
      );
    };
    if (props.path && props.path.length >= 2) return draw(props.path);
    directions
      .get(a, b, "bicycle")
      .then((d) => draw(d.path.length >= 2 ? d.path : [a, b]))
      .catch(() => draw([a, b]));
  });
  return (
    <Show
      when={props.from ?? props.to}
      fallback={<div class="h-[190px] shrink-0 bg-surface2" aria-hidden="true" />}
    >
      <div
        ref={container}
        class="pointer-events-none relative h-[190px] shrink-0 overflow-hidden bg-surface2"
        aria-label="Carte du trajet"
        role="img"
      />
    </Show>
  );
}
